import { FlightLevel, FlightLevel_Unit, FlightLevelBand, TimePeriod } from 'app/model/CORE';
import FlightLevelUtils from 'app/common/util/FlightLevelUtils';
import TimeUtils from 'app/common/util/TimeUtils';
import MathUtils from 'app/common/util/MathUtils';

export class Range {
    readonly from: number;
    readonly to: number;
    readonly size: number;

    constructor(from: number, to: number) {
        this.from = from;
        this.to = to;
        this.size = to - from;
    }

    /**
     * Return true if the value is in the range.
     * Note : Ranges are INCLUSIVE.
     * @param value
     */
    contains(value: number) {
        if (this.from <= this.to) {
            return value >= this.from && value <= this.to;
        } else {
            return value >= this.to && value <= this.from;
        }
    }

    /**
     * Return the first value in the range that is aligned on an 'interval' boundary
     * @param interval
     */
    getFirstValue(interval: number) {
        if (this.from <= this.to) {
            return MathUtils.nearestUpperInterval(this.from, interval);
        } else {
            return MathUtils.nearestLowerInterval(this.from, interval);
        }
    }

    /**
     * Return the last value in the range that is aligned on an interval boundary.
     * @param interval
     */
    getLastValue(interval: number) {
        if (this.from <= this.to) {
            return MathUtils.nearestLowerInterval(this.to, interval);
        } else {
            return MathUtils.nearestUpperInterval(this.to, interval);
        }
    }

    /**
     * Return the range in increasing FROM->TO
     */
    toIncreasingRange(): Range {
        return this.from <= this.to ? this : new Range(this.to, this.from);
    }

    toString() {
        return '[' + this.from + ',' + this.to + ']';
    }
}

/**
 * A logical scale, where by convention
 *  * source - represents the graphical coordinates
 *  * target - represents a business domain (e.g. time period, FL range)
 *  * targetLimits - the maximum possible extent of target
 *
 */
export class Scale {
    source: Range; // not readonly on purpose - since autosizer forces us to update directly the width/height
    readonly target: Range;
    readonly targetLimits: Range;
    readonly targetMinSize: number;

    constructor(source: Range, target: Range, targetLimits?: Range, targetMinSize: number = 0) {
        this.source = source;
        this.target = target;
        this.targetLimits = targetLimits;
        this.targetMinSize = targetMinSize;
    }

    toTarget(value: number): number {
        return ((value - this.source.from) / this.source.size) * this.target.size + this.target.from;
    }

    toSource(value: number): number {
        return ((value - this.target.from) / this.target.size) * this.source.size + this.source.from;
    }

    toSourceLength(targetLength: number) {
        return (targetLength / this.target.size) * this.source.size;
    }

    toTargetLength(sourceLength: number) {
        return (sourceLength / this.source.size) * this.target.size;
    }

    getIncreasingSource(): Range {
        return this.source.toIncreasingRange();
    }

    shiftTargetByAmount(delta: number): Range {
        let from = this.target.from + delta;
        let to = this.target.to + delta;
        if (!this.isInTargetLimits(from)) {
            from = this.targetLimits.from;
            to = from + (this.target.to - this.target.from);
        } else if (!this.isInTargetLimits(to)) {
            to = this.targetLimits.to;
            from = to - (this.target.to - this.target.from);
        }
        return new Range(from, to);
    }

    shiftTargetFromByAmount(delta: number): Range {
        const from = this.target.from + delta;
        const size = this.target.to - from;
        if (Math.abs(size) < Math.abs(this.targetMinSize)) {
            return new Range(this.target.to - this.targetMinSize, this.target.to);
        } else if (!this.isInTargetLimits(from)) {
            return new Range(this.targetLimits.from, this.target.to);
        } else if (Math.sign(size) !== Math.sign(this.target.to - this.target.from)) {
            return new Range(this.target.to - this.targetMinSize, this.target.to);
        } else {
            return new Range(from, this.target.to);
        }
    }

    shiftTargetToByAmount(delta: number): Range {
        const to = this.target.to + delta;
        const size = to - this.target.from;
        if (Math.abs(size) < Math.abs(this.targetMinSize)) {
            return new Range(this.target.from, this.target.from + this.targetMinSize);
        } else if (!this.isInTargetLimits(to)) {
            return new Range(this.target.from, this.targetLimits.to);
        } else if (Math.sign(size) !== Math.sign(this.target.to - this.target.from)) {
            return new Range(this.target.from, this.target.from + this.targetMinSize);
        } else {
            return new Range(this.target.from, to);
        }
    }

    toString() {
        return (
            '[Scale source=' +
            this.source?.toString() +
            ' target=' +
            this.target?.toString() +
            ' targetLimits=' +
            this.targetLimits?.toString() +
            ' targetMinSize=' +
            this.targetMinSize
        );
    }

    private isInTargetLimits(val: number) {
        if (this.targetLimits) {
            if (this.targetLimits.from <= this.targetLimits.to) {
                return val >= this.targetLimits.from && val <= this.targetLimits.to;
            } else {
                return val >= this.targetLimits.to && val <= this.targetLimits.from;
            }
        } else {
            return true;
        }
    }
}

/**
 * Convenience Class that maps Flight Levels (as feet) onto a Scale.
 */
export class LevelScale {
    private scale: Scale;

    private constructor() {}

    static newLevelScale(
        source: Range,
        target: FlightLevelBand,
        targetLimits?: FlightLevelBand,
        minNumLevels: number = 0,
    ) {
        const levelScale = new LevelScale();
        levelScale.scale = new Scale(
            source,
            levelScale.toRange(target),
            levelScale.toRange(targetLimits),
            levelScale.toTargetSize(minNumLevels),
        );
        return levelScale;
    }

    static forNewTarget(levelScale: LevelScale, target: Range): LevelScale {
        const newLevelScale: LevelScale = new LevelScale();
        newLevelScale.scale = new Scale(
            levelScale.scale.source,
            target,
            levelScale.scale.targetLimits,
            levelScale.scale.targetMinSize,
        );
        return newLevelScale;
    }

    static forNewSource(levelScale: LevelScale, newSource: Range): LevelScale {
        const newLevelScale: LevelScale = new LevelScale();
        newLevelScale.scale = new Scale(
            newSource,
            levelScale.scale.target,
            levelScale.scale.targetLimits,
            levelScale.scale.targetMinSize,
        );
        return newLevelScale;
    }

    getScale(): Scale {
        return this.scale;
    }

    setSource(newSource: Range) {
        this.scale.source = newSource;
    }

    toSource(level: FlightLevel): number {
        return this.scale.toSource(FlightLevelUtils.getFeetValue(level));
    }

    toTarget(value: number): FlightLevel {
        return this.toFlightLevel(this.scale.toTarget(value));
    }

    toRange(levelBand: FlightLevelBand) {
        if (levelBand) {
            return new Range(this.toValue(levelBand.min), this.toValue(levelBand.max));
        }
    }

    toFlightLevelBand(range: Range): FlightLevelBand {
        return { min: this.toFlightLevel(range.from), max: this.toFlightLevel(range.to) };
    }

    toValue(level: FlightLevel): number {
        return FlightLevelUtils.getFeetValue(level);
    }

    toFlightLevel(value: number): FlightLevel {
        return FlightLevelUtils.newFlightLevel(value, FlightLevel_Unit.ALTITUDE_FEET);
    }

    toTargetSize(numLevels: number): number {
        return numLevels * FlightLevelUtils.FEET_PER_FLIGHT_LEVEL;
    }
}

/**
 * Convenience class that maps Time/TimePeriods onto a Scale.
 */
export class TimeScale {
    private scale: Scale;
    private zeroTime: Date;

    private constructor() {}

    static newTimeScale(
        source: Range,
        target: TimePeriod,
        targetLimits?: TimePeriod,
        minSizeInMin?: number,
    ): TimeScale {
        const newTimeScale = new TimeScale();
        newTimeScale.zeroTime = TimeUtils.withoutTimeOfDay(targetLimits?.from || target.from);
        newTimeScale.scale = new Scale(
            source,
            newTimeScale.toRange(target),
            newTimeScale.toRange(targetLimits),
            minSizeInMin * 60,
        );
        return newTimeScale;
    }

    static forNewTarget(timeScale: TimeScale, newTarget: Range): TimeScale {
        const newTimeScale = new TimeScale();
        newTimeScale.zeroTime = timeScale.zeroTime;
        newTimeScale.scale = new Scale(
            timeScale.scale.source,
            newTarget,
            timeScale.scale.targetLimits,
            timeScale.scale.targetMinSize,
        );
        return newTimeScale;
    }

    static forNewSource(timeScale: TimeScale, newSource: Range): TimeScale {
        const newTimeScale = new TimeScale();
        newTimeScale.zeroTime = timeScale.zeroTime;
        newTimeScale.scale = new Scale(
            newSource,
            timeScale.scale.target,
            timeScale.scale.targetLimits,
            timeScale.scale.targetMinSize,
        );
        return newTimeScale;
    }

    setSource(newSource: Range) {
        this.scale.source = newSource;
    }

    getScale(): Scale {
        return this.scale;
    }

    toSource(time: Date): number {
        return this.scale.toSource(this.toValue(time));
    }

    toTarget(value: number): Date {
        return this.toDate(this.scale.toTarget(value));
    }

    toRange(timePeriod: TimePeriod) {
        if (timePeriod) {
            return new Range(this.toValue(timePeriod.from), this.toValue(timePeriod.to));
        }
    }

    toTimePeriod(range: Range): TimePeriod {
        return { from: this.toDate(range.from), to: this.toDate(range.to) };
    }

    toValue(time: Date): number {
        return TimeUtils.diffInSeconds(time, this.zeroTime);
    }

    toDate(value: number): Date {
        return TimeUtils.addSeconds(this.zeroTime, value);
    }
}
